import { mapActions, mapGetters } from 'vuex';
import { dateToString, isMarketSingle, toCents } from '@/utils/helpers';
import smallModal from '@/components/generics/modal/small/small.vue';
import { MODALS } from '@/components/app/app-modals/modals-constants';

export default {
  components: {
    smallModal,
  },
  props: {
    scrollable: {
      type: Boolean,
      default: false,
    },
    minimumRows: {
      type: Number,
      default: 3,
    },
    contract: {
      type: Object,
      required: true,
    },
    market: {
      type: Object,
      required: true,
    },
    buyOffers: {
      type: Array,
      required: true,
      default: [],
    },
    sellOffers: {
      type: Array,
      required: true,
      default: [],
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    operationType: {
      type: String,
      default: 'buy',
      validator: value => ['buy', 'sell'].indexOf(value) >= 0,
    },
  },
  data() {
    return {
      deleteOfferId: null,
      isCancelling: false,
    };
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapGetters('app', ['isCancelOrdersEnabled']),
    classModifiers() {
      return {
        'order-book-my-orders-sushi': true,
        'order-book-my-orders-sushi--empty': this.isEmpty,
      };
    },
    tableModifiers() {
      return {
        'order-book-sushi__table': true,
        'order-book-sushi__table--scrollable': this.scrollable,
      };
    },
    bodyModifiers() {
      return {
        'order-book-sushi__body': true,
        'order-book-sushi__body--scrollable': this.scrollable,
        'order-book-my-orders-sushi__body--empty': this.isEmpty,
      };
    },
    emptyStateText() {
      return this.isLoggedIn ? '(No Orders)' : '(Not Logged In)';
    },
    activeOrderSide() {
      return this.operationType === 'sell' ? 'sell' : 'buy';
    },
    emptyOrdersText() {
      return `You have no ${this.activeOrderSide} orders.`;
    },
    buyHeaderModifiers() {
      return {
        'order-book-sushi__header-cell': true,
        'order-book-sushi__header-cell--with-avail-matches': true,
        'order-book-sushi__header-cell--active': this.activeOrderSide === 'buy',
      };
    },
    sellHeaderModifiers() {
      return {
        'order-book-sushi__header-cell': true,
        'order-book-sushi__header-cell--with-avail-matches': true,
        'order-book-sushi__header-cell--active': this.activeOrderSide === 'sell',
      };
    },
    isMarketSingle() {
      return isMarketSingle(this.market);
    },
    imageUrl() {
      return this.contract.contractImageSmallUrl || this.contract.contractImageUrl;
    },
    contractImageStyleModifiers() {
      return {
        'background-image': `url(${this.imageUrl})`,
      };
    },
    buyOffersMapped() {
      return [...this.buyOffers].map((offer) => {
        return {
          ...offer,
          pricePerShare: toCents(offer.pricePerShare),
        };
      }).sort((prev, next) => next.pricePerShare - prev.pricePerShare);
    },
    sellOffersMapped() {
      return [...this.sellOffers].map((offer) => {
        return {
          ...offer,
          pricePerShare: toCents(offer.pricePerShare),
        };
      }).sort((prev, next) => prev.pricePerShare - next.pricePerShare);
    },
    displayRows() {
      const rowCount = Math.max(this.buyOffersMapped.length, this.sellOffersMapped.length, this.scrollable ? this.minimumRows : 0);
      return Array.from({ length: rowCount }, (item, index) => {
        return {
          buy: this.buyOffersMapped[index] || null,
          sell: this.sellOffersMapped[index] || null,
          showBuyEmpty: !this.isEmpty && this.buyOffersMapped.length === 0 && index === 0,
          showSellEmpty: !this.isEmpty && this.sellOffersMapped.length === 0 && index === 0,
        };
      });
    },
    isEmpty() {
      return this.buyOffersMapped.length === 0 && this.sellOffersMapped.length === 0;
    },
    selectedOffer() {
      return [...this.buyOffersMapped, ...this.sellOffersMapped].find(offer => offer.offerId === this.deleteOfferId) || null;
    },
    showCancelModal() {
      return !!this.selectedOffer;
    },
    showProcessedNotice() {
      return [...this.buyOffersMapped, ...this.sellOffersMapped].some(offer => !offer.isProcessed);
    },
  },
  methods: {
    ...mapActions('trade', ['cancelOffer']),
    ...mapActions('notifications', ['setErrorSideNotification', 'setSuccessSideNotification']),
    ...mapActions('popup', ['setCurrentPopup']),
    loginClicked() {
      this.setCurrentPopup(MODALS.LOGIN);
    },
    getDateFull(item) {
      return dateToString(item.dateCreated);
    },
    offerFullInfoDisplay(item) {
      const quantity = item.remainingQuantity.toLocaleString();
      const price = `${item.pricePerShare}&cent;`;
      switch (item.tradeType) {
        case 0: return `Buy ${quantity} NO at Max ${price}`;
        case 1: return `Buy ${quantity} YES at Max ${price}`;
        case 2: return `Sell ${quantity} NO at Min ${price}`;
        case 3: return `Sell ${quantity} YES at Min ${price}`;
        default: return '';
      }
    },
    cancelClicked(offerId) {
      this.deleteOfferId = offerId;
    },
    closeCancelModal() {
      this.deleteOfferId = null;
      this.isCancelling = false;
    },
    confirmCancelClicked() {
      if (!this.selectedOffer || this.isCancelling) {
        return;
      }

      if (!this.isCancelOrdersEnabled) {
        this.setErrorSideNotification('The site is in Maintenance so we are unable to cancel your order at this time. Please try again later.');
        this.closeCancelModal();
        return;
      }

      this.isCancelling = true;
      this.cancelOffer(this.selectedOffer.offerId)
        .then(() => {
          this.setSuccessSideNotification('Your order has been removed.');
          this.closeCancelModal();
          this.$emit('offerCanceled');
        })
        .catch(() => {
          this.closeCancelModal();
          this.setErrorSideNotification('Unable to cancel your order right now.');
        });
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/purchase/sushi/order-book-sushi/order-book-my-orders-sushi/order-book-my-orders-sushi.js